/**
 * Application shell: left drawer (centered logo + navigation, with the
 * collapse toggle and the version/copyright stamp pinned at its foot) —
 * permanent and collapsible on desktop (the rail shows the ammonite mark
 * alone), a temporary overlay behind a hamburger on phones (a permanent
 * drawer would eat the viewport) — top bar with global search, the
 * pending-approvals bell and the avatar menu (profile, language, theme,
 * session), and the stale-build banner.
 */
import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import AutoModeIcon from "@mui/icons-material/AccountTree";
import BuildIcon from "@mui/icons-material/Handyman";
import RunsIcon from "@mui/icons-material/FormatListBulleted";
import ApprovalsIcon from "@mui/icons-material/FactCheck";
import ChatIcon from "@mui/icons-material/SupportAgent";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BusinessIcon from "@mui/icons-material/Business";
import { GlobalSearch } from "../components/GlobalSearch";
import { CustomerSelect, EditModeIcon } from "../components/CustomerSelect";
import { useCustomer } from "../hooks/useCustomer";
import { ApprovalsBell } from "../components/ApprovalsBell";
import { UserMenu } from "../auth/UserMenu";
import { ChangePasswordDialog } from "../auth/ChangePasswordDialog";
import { useAuth } from "../auth/useAuth";
import { api } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { APPROVALS_POLL_MS, HEALTH_POLL_MS } from "../lib/config";
import logo from "../assets/cdm-logo.png";
import logoIcon from "../assets/cdm-logo-icon.png";

const DRAWER_WIDTH = 248;
const RAIL_WIDTH = 64;
const COPYRIGHT_YEAR = new Date().getFullYear();

export function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { tokens } = theme;
  const { isSysadmin, user } = useAuth();
  const { customers: myCustomers, current: currentCustomer, loading: customersLoading } =
    useCustomer();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [staleBuild, setStaleBuild] = useState(false);

  // Close the mobile overlay after any navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const healthQuery = useApiQuery(["health"], api.health, {
    pollMs: HEALTH_POLL_MS,
  });
  const knownBuildRef = useRef<string | null>(null);
  useEffect(() => {
    const build = healthQuery.data?.build;
    if (!build) return;
    if (knownBuildRef.current === null) {
      knownBuildRef.current = build;
    } else if (knownBuildRef.current !== build) {
      setStaleBuild(true);
    }
  }, [healthQuery.data]);
  const build = knownBuildRef.current ?? healthQuery.data?.build ?? "";

  // Same queryKey as the ApprovalsBell — react-query dedupes the fetches.
  const pendingQuery = useApiQuery(["approvals", "pending"], api.pending, {
    pollMs: APPROVALS_POLL_MS,
  });
  const pendingCount = pendingQuery.data?.length ?? 0;

  const width = open ? DRAWER_WIDTH : RAIL_WIDTH;
  const sidebarStamp = `Ciments du Maroc · ${t("app.build")} ${build || "…"} · © ${COPYRIGHT_YEAR}`;
  const nav = [
    { to: "/", icon: <DashboardIcon />, label: t("nav.dashboard") },
    { to: "/tool", icon: <BuildIcon />, label: t("nav.singleTool") },
    { to: "/wizard", icon: <AutoModeIcon />, label: t("nav.wizard") },
    { to: "/runs", icon: <RunsIcon />, label: t("nav.runs") },
    {
      to: "/approvals",
      icon: (
        <Badge badgeContent={pendingCount} color="warning" max={99}>
          <ApprovalsIcon />
        </Badge>
      ),
      label: t("nav.approvals"),
    },
    { to: "/audit", icon: <ChatIcon />, label: t("nav.audit") },
    ...(isSysadmin
      ? [
          { to: "/admin/users", icon: <ManageAccountsIcon />, label: t("nav.users") },
          { to: "/admin/customers", icon: <BusinessIcon />, label: t("nav.customers") },
        ]
      : []),
  ];
  // the pages work on ONE customer: without one chosen there is nothing to
  // show, except the administration pages themselves
  const onAdminPage = location.pathname.startsWith("/admin");
  const customerGate = onAdminPage
    ? null
    : customersLoading
      ? "loading"
      : myCustomers.length === 0
        ? "none"
        : currentCustomer === null
          ? "choose"
          : null;

  // Drawer contents: expanded (labels visible) or rail (icons only).
  const drawerContent = (expanded: boolean) => (
    <>
      {/* Centered brand block: full logo when expanded, the ammonite mark
          alone when collapsed to the rail. */}
      <Toolbar disableGutters sx={{ px: 1, justifyContent: "center" }}>
        <Box
          component="img"
          src={expanded ? logo : logoIcon}
          alt="Ciments du Maroc"
          sx={{
            height: expanded ? 44 : 34,
            maxWidth: expanded ? 180 : 40,
            objectFit: "contain",
            bgcolor: "common.white",
            borderRadius: 1,
            p: 0.5,
          }}
        />
      </Toolbar>
      <Divider sx={{ borderColor: tokens.sidebar.divider }} />
      <List sx={{ pt: 1 }}>
        {nav.map((item) => {
          const selected =
            item.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.to);
          return (
            <Tooltip
              key={item.to}
              title={expanded ? "" : item.label}
              placement="right"
            >
              <ListItemButton
                component={NavLink}
                to={item.to}
                selected={selected}
                sx={{
                  minHeight: 46,
                  px: expanded ? 2 : 1.5,
                  justifyContent: expanded ? "flex-start" : "center",
                  color: tokens.sidebar.text,
                  "&.Mui-selected": {
                    bgcolor: tokens.sidebar.selectedBg,
                    color: "common.white",
                    borderLeft: "3px solid",
                    borderColor: "secondary.main",
                  },
                }}
              >
                <ListItemIcon
                  sx={{ color: "inherit", minWidth: expanded ? 40 : "auto" }}
                >
                  {item.icon}
                </ListItemIcon>
                {expanded && <ListItemText primary={item.label} />}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ borderColor: tokens.sidebar.divider }} />
      {!isMobile && (
        <ListItemButton
          onClick={() => setOpen(!open)}
          sx={{
            justifyContent: expanded ? "flex-start" : "center",
            color: tokens.sidebar.textMuted,
            minHeight: 46,
            flexGrow: 0,
          }}
          aria-label={t("nav.collapse")}
        >
          <ListItemIcon sx={{ color: "inherit", minWidth: expanded ? 40 : "auto" }}>
            {expanded ? <ChevronLeftIcon /> : <MenuIcon />}
          </ListItemIcon>
          {expanded && <ListItemText primary={t("nav.collapse")} />}
        </ListItemButton>
      )}
      {/* Version + copyright live at the foot of the menu (the rail is too
          narrow — the tooltip on hover still tells the story). */}
      <Tooltip title={expanded ? "" : sidebarStamp} placement="right">
        <Box
          sx={{
            px: 1,
            py: 1.25,
            textAlign: "center",
            color: tokens.sidebar.textMuted,
            borderTop: 1,
            borderColor: tokens.sidebar.divider,
          }}
        >
          {expanded ? (
            <>
              <Typography
                variant="caption"
                sx={{ display: "block", fontWeight: 600, color: tokens.sidebar.text }}
              >
                Ciments du Maroc
              </Typography>
              <Typography variant="caption" sx={{ display: "block", fontSize: 11 }}>
                {t("app.build")} {build || "…"} · © {COPYRIGHT_YEAR}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" sx={{ fontSize: 11 }}>
              ©
            </Typography>
          )}
        </Box>
      </Tooltip>
    </>
  );

  const drawerPaperSx = {
    overflowX: "hidden",
    transition: "width 200ms",
    bgcolor: tokens.sidebar.bg,
    color: "common.white",
    borderRight: "none",
  } as const;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": { ...drawerPaperSx, width: DRAWER_WIDTH },
          }}
        >
          {drawerContent(true)}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width,
            flexShrink: 0,
            whiteSpace: "nowrap",
            transition: "width 200ms",
            "& .MuiDrawer-paper": { ...drawerPaperSx, width },
          }}
        >
          {drawerContent(open)}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}
        >
          <Toolbar sx={{ gap: 2 }}>
            {isMobile && (
              <IconButton
                edge="start"
                aria-label={t("nav.openMenu")}
                onClick={() => setMobileOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
            <CustomerSelect />
            <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-start" }}>
              <GlobalSearch />
            </Box>
            <EditModeIcon />
            <ApprovalsBell />
            <UserMenu />
          </Toolbar>
        </AppBar>

        {staleBuild && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                {t("app.refresh")}
              </Button>
            }
            sx={{ borderRadius: 0 }}
          >
            {t("app.newVersion")}
          </Alert>
        )}

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          {customerGate === "loading" ? (
            <LinearProgress />
          ) : customerGate === "none" ? (
            <Alert severity="warning">{t("customer.noneHint")}</Alert>
          ) : customerGate === "choose" ? (
            <Alert severity="info">{t("customer.chooseHint")}</Alert>
          ) : (
            // keyed by the customer: a switch remounts the page, so no state of
            // the previous customer (a selected run, a form) survives
            <Outlet key={currentCustomer?.id ?? "none"} />
          )}
        </Box>
      </Box>

      {/* A user signed in with a temporary password must set a real one before
          doing anything — the dialog is non-dismissable until they do. */}
      {user?.must_change_password && (
        <ChangePasswordDialog open forced onClose={() => undefined} />
      )}
    </Box>
  );
}
